import { Box, Stack, Typography } from "@mui/joy";
import Button from "@mui/joy/Button";
import { ProgramContentType } from "data/BlockComponent";
import { MultipleSelectionType } from "data/CellComponentV2";
import * as _ from "lodash";
import { setProgramContentData } from "logic/logics";
import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_INCORRECT_MESSAGE = "다시 생각해보세요";

function normalizeIndices(indices?: number[]) {
  if (!Array.isArray(indices)) {
    return [];
  }

  return Array.from(
    new Set(
      indices
        .filter((index) => typeof index === "number" && Number.isFinite(index))
        .map((index) => Math.floor(index))
    )
  ).sort((left, right) => left - right);
}

export default function ButtonGroupMultipleChoiceCellV2({
  initialSelectedIndices,
  requiredSelectionCount,
  correctOptionIndices,
  incorrectMessage,
  blockIndex,
  options,
  setData,
  lineIndex,
  cellIndex,
}: {
  initialSelectedIndices?: number[];
  requiredSelectionCount?: number;
  correctOptionIndices?: number[];
  incorrectMessage?: string;
  blockIndex: number;
  options: string[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
}) {
  const [selectedIndices, setSelectedIndices] = useState<number[]>(
    initialSelectedIndices ?? []
  );
  const prevInitialRef = useRef<number[] | undefined>(initialSelectedIndices);
  const normalizedCorrectOptionIndices = useMemo(
    () =>
      normalizeIndices(correctOptionIndices).filter(
        (index) => index >= 0 && index < options.length
      ),
    [correctOptionIndices, options.length]
  );
  const normalizedSelectedIndices = useMemo(
    () =>
      normalizeIndices(selectedIndices).filter(
        (index) => index >= 0 && index < options.length
      ),
    [options.length, selectedIndices]
  );
  const normalizedRequiredSelectionCount = useMemo(() => {
    if (
      typeof requiredSelectionCount === "number" &&
      Number.isFinite(requiredSelectionCount) &&
      requiredSelectionCount > 0
    ) {
      return Math.floor(requiredSelectionCount);
    }

    if (normalizedCorrectOptionIndices.length > 0) {
      return normalizedCorrectOptionIndices.length;
    }

    return undefined;
  }, [normalizedCorrectOptionIndices.length, requiredSelectionCount]);
  const shouldShowIncorrectMessage = useMemo(() => {
    if (
      normalizedCorrectOptionIndices.length === 0 ||
      normalizedSelectedIndices.length === 0
    ) {
      return false;
    }

    if (
      normalizedRequiredSelectionCount !== undefined &&
      normalizedSelectedIndices.length < normalizedRequiredSelectionCount
    ) {
      return false;
    }

    return !_.isEqual(normalizedSelectedIndices, normalizedCorrectOptionIndices);
  }, [
    normalizedCorrectOptionIndices,
    normalizedRequiredSelectionCount,
    normalizedSelectedIndices,
  ]);
  const displayedIncorrectMessage =
    typeof incorrectMessage === "string" && incorrectMessage.trim().length > 0
      ? incorrectMessage.trim()
      : DEFAULT_INCORRECT_MESSAGE;

  useEffect(() => {
    if (!_.isEqual(initialSelectedIndices, prevInitialRef.current)) {
      setSelectedIndices(initialSelectedIndices ?? []);
      prevInitialRef.current = initialSelectedIndices;
    }
  }, [initialSelectedIndices]);

  const handleChange = (nextSelectedIndices?: number[]) => {
    if (!_.isEqual(initialSelectedIndices, nextSelectedIndices)) {
      setProgramContentData({
        setData,
        blockIndex,
        lineIndex,
        cellIndex,
        newlyAddedData: {
          value: nextSelectedIndices
            ?.map((selectedIndex) => options[selectedIndex])
            .join("&&"),
          selectedIndices: nextSelectedIndices,
        },
      });
      setData((data) => {
        if (
          data &&
          data[blockIndex] &&
          data[blockIndex].lines &&
          data[blockIndex].lines[lineIndex] &&
          data[blockIndex].lines[lineIndex][cellIndex]
        ) {
          const originalCurrentContent = (data[blockIndex].lines[lineIndex][
            cellIndex
          ].content || {}) as MultipleSelectionType;

          const data_temp = _.cloneDeep(data);
          const newlySelectedIndex = nextSelectedIndices?.find(
            (index) => !initialSelectedIndices?.includes(index)
          );
          const newlyUnselectedIndex = initialSelectedIndices?.find(
            (index) => !nextSelectedIndices?.includes(index)
          );
          if (
            newlySelectedIndex !== undefined &&
            originalCurrentContent.options
          ) {
            const selectedOption =
              originalCurrentContent.options[newlySelectedIndex];
            const additionalContent = selectedOption?.additionalContent;
            if (additionalContent) {
              data_temp[blockIndex].lines.splice(
                data_temp[blockIndex].lines.length,
                0,
                ...additionalContent
              );
            }
          }
          if (
            newlyUnselectedIndex !== undefined &&
            originalCurrentContent.options
          ) {
            const unSelectedOption =
              originalCurrentContent.options[newlyUnselectedIndex];
            const additionalContent = unSelectedOption.additionalContent;
            additionalContent?.forEach((line) => {
              const hideCellId = line[0]?.content.id;
              if (hideCellId) {
                const hideCellIndex = data_temp[blockIndex].lines.findIndex(
                  (line) => line.find((cell) => cell.content.id === hideCellId)
                );
                if (hideCellIndex > -1) {
                  data_temp[blockIndex].lines.splice(hideCellIndex, 1);
                }
              }
            });
          }

          return data_temp;
        }

        return data;
      });
    }
  };

  useEffect(() => {
    handleChange(selectedIndices);
  }, [selectedIndices]);

  return (
    <Stack spacing={0.75} sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        sx={{
          width: "100%",
          whiteSpace: "nowrap",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {options.map((option, optionIndex) => (
          <Box
            key={`buttongroup_option_${blockIndex}_${lineIndex}_${cellIndex}_${optionIndex}`}
            sx={{ flexGrow: 1 }}
          >
            <Button
              fullWidth
              variant="Selection"
              color={
                selectedIndices.includes(optionIndex) ? "primary" : "secondary"
              }
              value={option}
              onClick={(event) => {
                if (!selectedIndices.includes(optionIndex)) {
                  setSelectedIndices((currentSelectedIndices) => {
                    if (
                      currentSelectedIndices.length === requiredSelectionCount
                    ) {
                      const nextSelectedIndices = _.cloneDeep(
                        currentSelectedIndices
                      );
                      nextSelectedIndices.splice(
                        currentSelectedIndices.length - 1,
                        1,
                        optionIndex
                      );

                      return nextSelectedIndices;
                    }

                    return [...currentSelectedIndices, optionIndex];
                  });
                } else {
                  setSelectedIndices((currentSelectedIndices) =>
                    currentSelectedIndices.filter(
                      (index) => index !== optionIndex
                    )
                  );
                }
                (event.currentTarget as HTMLElement).blur();
              }}
            >
              {option}
            </Button>
          </Box>
        ))}
      </Stack>
      {shouldShowIncorrectMessage ? (
        <Typography level="body-sm" color="danger">
          {displayedIncorrectMessage}
        </Typography>
      ) : null}
    </Stack>
  );
}

